import { EntityType, NegotiationDetail, NegotiationType } from 'api'
import { noop } from 'lodash'
import { isCounterpartySelectedField } from 'selectors/negotiations/negotiationCounterpartySelectors'
import { RepositoryActions } from 'store/repository'
import { isNotNil } from 'utils/predicate'

interface NegotiationActionRendererChangeHandlerStrategyOptions {
  isGroup: boolean
  onUpdateMeta: typeof RepositoryActions.updateMeta
  currentNegotiationDetail: NegotiationDetail
  selectedNegotiationDetail: NegotiationDetail | undefined
  selectedNegotiationDetailSummary: NegotiationDetail | undefined
}
type NegotiationActionRendererChangeHandler = (isChecked?: boolean) => void

type NegotiationActionRendererChangeHandlerFactory = (
  props: NegotiationActionRendererChangeHandlerStrategyOptions
) => NegotiationActionRendererChangeHandler

type NegotiationActionRendererChangeHandlerStrategy<
  T extends NegotiationActionRendererChangeHandler = NegotiationActionRendererChangeHandler
> = (props: NegotiationActionRendererChangeHandlerStrategyOptions) => T

export const getChangeHandler: NegotiationActionRendererChangeHandlerStrategy = options => {
  let factory: NegotiationActionRendererChangeHandlerFactory

  switch (options.currentNegotiationDetail?.negotiation.negotiationType) {
    case NegotiationType.CONTINGENT_SUMMARY:
      factory = AONSummaryNegotiationActionRendererChangeHandlerFactory
      break
    case NegotiationType.CONTINGENT_ITEM:
      factory = NoOpFactory // contingent items should always be disabled but us noop as a safeguard to prevent default
      break
    default:
      factory = DefaultNegotiationActionRendererChangeHandlerFactory
  }

  return factory(options)
}

const NoOpFactory: NegotiationActionRendererChangeHandlerFactory = () => noop

const DefaultNegotiationActionRendererChangeHandlerFactory: NegotiationActionRendererChangeHandlerFactory = options => (
  isChecked = false
) => {
  const {
    onUpdateMeta,
    selectedNegotiationDetailSummary,
    selectedNegotiationDetail,
    isGroup,
    currentNegotiationDetail: { negotiation }
  } = options

  if (!isGroup && selectedNegotiationDetail) {
    onUpdateMeta(EntityType.NEGOTIATION, selectedNegotiationDetail.negotiation.negotiationId, isCounterpartySelectedField, false, {
      ownOrder: true,
      persist: true
    })
  }

  if (isGroup && isNotNil(selectedNegotiationDetailSummary)) {
    onUpdateMeta(EntityType.NEGOTIATION, selectedNegotiationDetailSummary.negotiation.negotiationId, isCounterpartySelectedField, false, {
      ownOrder: true,
      persist: true
    })
  }

  onUpdateMeta(EntityType.NEGOTIATION, negotiation.negotiationId, isCounterpartySelectedField, isChecked, {
    ownOrder: true,
    persist: true
  })
}

const AONSummaryNegotiationActionRendererChangeHandlerFactory: NegotiationActionRendererChangeHandlerFactory = options => () => {
  const setNegotiationSelected = (negotiationId: string, isSelected: boolean) =>
    onUpdateMeta(EntityType.NEGOTIATION, negotiationId, isCounterpartySelectedField, isSelected, {
      ownOrder: true,
      persist: true
    })

  const {
    onUpdateMeta,
    selectedNegotiationDetailSummary,
    currentNegotiationDetail: { negotiation: currentNegotiation }
  } = options

  if (isNotNil(selectedNegotiationDetailSummary)) {
    // there is currently a summary selected
    const selectedNegotiation = selectedNegotiationDetailSummary.negotiation

    if (selectedNegotiation.negotiationId === currentNegotiation.negotiationId) {
      // selected summary is the current row so unselect it and it's children
      ;[currentNegotiation.negotiationId, ...currentNegotiation.negotiationMeta.childNegotiationIds].map(id =>
        setNegotiationSelected(id, false)
      )
    } else if (selectedNegotiation.negotiationId !== currentNegotiation.negotiationId) {
      // selected summary is not the current row, so unselect currently selected and it's children, then select current and it's children

      ;[selectedNegotiation.negotiationId, ...selectedNegotiation.negotiationMeta.childNegotiationIds].map(id =>
        setNegotiationSelected(id, false)
      )
      ;[currentNegotiation.negotiationId, ...currentNegotiation.negotiationMeta.childNegotiationIds].map(id =>
        setNegotiationSelected(id, true)
      )
    }
  } else {
    // there is no summary currently selected so select this one and it's children
    ;[currentNegotiation.negotiationId, ...currentNegotiation.negotiationMeta.childNegotiationIds].map(id =>
      setNegotiationSelected(id, true)
    )
  }
}
